import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Lock, Mail, ArrowLeft, Loader2, Smartphone, KeyRound } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// DEV MODE: Auto-login pour faciliter les tests (desactive en production)

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [step, setStep] = useState(1); // 1 = credentials, 2 = 2FA
  const [useBackup, setUseBackup] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const autoLoginAttempted = useRef(false);

  const submitForgot = async () => {
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/forgot-password`, { email });
      setForgotSent(res.data.message);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur, reessayez plus tard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoLoginAttempted.current) return;
    autoLoginAttempted.current = true;
    if (localStorage.getItem('admin_token')) {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const submitCredentials = async () => {
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/login`, { email, password });
      if (res.data?.requires_2fa) {
        setStep(2);
        setLoading(false);
        return;
      }
      localStorage.setItem('admin_token', res.data.token);
      if (res.data.refresh_token) localStorage.setItem('admin_refresh_token', res.data.refresh_token);
      localStorage.setItem('admin_email', res.data.email);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de connexion');
      setLoading(false);
    }
  };

  const submitTotp = async () => {
    setError(''); setLoading(true);
    try {
      const payload = { email, password };
      if (useBackup) payload.backup_code = backupCode;
      else payload.totp_code = totpCode;
      const res = await axios.post(`${BACKEND_URL}/api/admin/login`, payload);
      localStorage.setItem('admin_token', res.data.token);
      if (res.data.refresh_token) localStorage.setItem('admin_refresh_token', res.data.refresh_token);
      localStorage.setItem('admin_email', res.data.email);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Code 2FA invalide');
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (showForgot) submitForgot();
    else if (step === 1) submitCredentials();
    else submitTotp();
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <button
          data-testid="admin-back-btn"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#595959] hover:text-[#2C3E30] mb-8 text-sm transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Retour au site
        </button>

        <div className="bg-white rounded-2xl p-8 md:p-10 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)]">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-[#2C3E30] flex items-center justify-center mx-auto mb-4">
              {step === 1 ? <Lock className="w-6 h-6 text-[#F2F0E9]" strokeWidth={1.5} /> : <Smartphone className="w-6 h-6 text-[#F2F0E9]" strokeWidth={1.5} />}
            </div>
            <h1 className="font-['Playfair_Display'] text-2xl text-[#2C3E30]">
              {step === 1 ? 'Administration' : 'Verification 2FA'}
            </h1>
            <p className="text-[#595959] text-sm mt-1">
              {step === 1 ? 'Le Domaine du Pilat' : useBackup ? 'Saisissez un code de recuperation' : 'Ouvrez votre appli d\'authentification'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid={step === 1 ? 'admin-login-step-1' : 'admin-login-step-2'}>
            {step === 1 && (
              <>
                <div>
                  <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A9A5B]" strokeWidth={1.5} />
                    <Input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@domaine.com" className="pl-10 rounded-lg h-11" autoComplete="username" />
                  </div>
                </div>
                {!showForgot && (
                <div>
                  <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Mot de passe</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A9A5B]" strokeWidth={1.5} />
                    <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe" className="pl-10 rounded-lg h-11" autoComplete="current-password" />
                  </div>
                  <button type="button" data-testid="forgot-password-link" onClick={() => { setShowForgot(true); setError(''); }} className="text-xs text-[#8A9A5B] hover:underline mt-2">
                    Mot de passe oublie ?
                  </button>
                </div>
                )}
                {showForgot && (
                  forgotSent ? (
                    <p data-testid="forgot-sent-msg" className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">{forgotSent} Pensez a verifier vos indesirables.</p>
                  ) : (
                    <p className="text-xs text-[#595959]">Saisissez votre email admin ci-dessus : un lien de reinitialisation (valable 1 h) vous sera envoye.</p>
                  )
                )}
              </>
            )}
            {step === 2 && !useBackup && (
              <div>
                <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Code a 6 chiffres</label>
                <div className="relative">
                  <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A9A5B]" strokeWidth={1.5} />
                  <Input data-testid="admin-totp-input" inputMode="numeric" pattern="\d{6}" maxLength={6} value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))} placeholder="123456" className="pl-10 rounded-lg h-11 font-mono text-center text-lg tracking-widest" autoComplete="one-time-code" autoFocus />
                </div>
                <button type="button" data-testid="admin-2fa-use-backup" onClick={() => { setUseBackup(true); setError(''); }} className="text-xs text-[#8A9A5B] hover:underline mt-2">
                  J&apos;ai perdu mon telephone — utiliser un code de recuperation
                </button>
              </div>
            )}
            {step === 2 && useBackup && (
              <div>
                <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Code de recuperation</label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A9A5B]" strokeWidth={1.5} />
                  <Input data-testid="admin-backup-input" value={backupCode} onChange={(e) => setBackupCode(e.target.value.toUpperCase())} placeholder="XXXXX-XXXXX" className="pl-10 rounded-lg h-11 font-mono text-center tracking-widest" autoFocus />
                </div>
                <button type="button" data-testid="admin-2fa-use-totp" onClick={() => { setUseBackup(false); setError(''); }} className="text-xs text-[#8A9A5B] hover:underline mt-2">
                  Utiliser mon appli d&apos;authentification a la place
                </button>
              </div>
            )}

            {error && <p data-testid="admin-login-error" className="text-[#993333] text-sm">{error}</p>}

            <Button data-testid="admin-login-btn" type="submit" disabled={loading || (showForgot && forgotSent) || (step === 2 && !useBackup && totpCode.length !== 6) || (step === 2 && useBackup && backupCode.length < 10)} className="w-full rounded-full py-6 text-base bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040] transition-transform active:scale-95 font-medium" size="lg">
              {loading ? (showForgot ? 'Envoi...' : 'Connexion...') : showForgot ? 'Envoyer le lien de reinitialisation' : step === 1 ? 'Se connecter' : 'Verifier et se connecter'}
            </Button>

            {showForgot && (
              <button type="button" data-testid="forgot-back-btn" onClick={() => { setShowForgot(false); setForgotSent(false); setError(''); }} className="w-full text-xs text-[#595959] hover:text-[#2C3E30]">
                &larr; Retour a la connexion
              </button>
            )}

            {step === 2 && (
              <button type="button" data-testid="admin-2fa-back" onClick={() => { setStep(1); setTotpCode(''); setBackupCode(''); setError(''); }} className="w-full text-xs text-[#595959] hover:text-[#2C3E30]">
                &larr; Retour a l&apos;identifiant
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
